import { saveAs } from "file-saver";
import { toPng } from "html-to-image";
import jsPDF from "jspdf";

export interface PdfExportOptions {
  element: HTMLElement;
  filename: string;
  title: string;
  subtitle?: string;
  contactName?: string;
  asOfDate: Date;
  locale: string;
}

const PAGE = { width: 297, height: 210, margin: 12 };
const HEADER_HEIGHT_MM = 16;
// Scale the captured image so charts and tables come out smaller in the PDF
// than on screen. 0.85 = 85% width, fits ~15% more content per page.
const CONTENT_SCALE = 0.85;
const LOGO_URL = `${process.env.PUBLIC_URL || ""}/borea_logo.png`;
const PRIMARY_COLOR: [number, number, number] = [45, 62, 100]; // tailwind primary-500

const loadImageAsDataUrl = async (url: string): Promise<string> => {
  const response = await fetch(url);
  const blob = await response.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

const loadImage = (dataUrl: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = dataUrl;
  });

const slicePngDataUrl = (
  source: HTMLImageElement,
  startY: number,
  height: number
): string => {
  const canvas = document.createElement("canvas");
  canvas.width = source.width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Failed to get 2D context");
  ctx.drawImage(
    source,
    0,
    startY,
    source.width,
    height,
    0,
    0,
    source.width,
    height
  );
  return canvas.toDataURL("image/png");
};

const drawHeader = async (
  pdf: jsPDF,
  opts: Pick<
    PdfExportOptions,
    "title" | "subtitle" | "contactName" | "asOfDate" | "locale"
  >
) => {
  const { margin } = PAGE;

  let logoRightEdge = margin;
  try {
    const logoDataUrl = await loadImageAsDataUrl(LOGO_URL);
    const logoImg = await loadImage(logoDataUrl);
    const logoHeight = 8;
    const logoWidth = (logoImg.width / logoImg.height) * logoHeight;
    pdf.addImage(
      logoDataUrl,
      "PNG",
      margin,
      margin,
      logoWidth,
      logoHeight
    );
    logoRightEdge = margin + logoWidth;
  } catch {
    // Logo failed to load — keep going without it.
  }

  // Title sits next to the logo.
  const baselineY = margin + 6;
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(13);
  pdf.setTextColor(...PRIMARY_COLOR);
  pdf.text(opts.title, logoRightEdge + 6, baselineY);

  // Metadata sits right-aligned on the same line as the title.
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(10);
  pdf.setTextColor(80, 80, 80);

  const metaParts: string[] = [];
  if (opts.contactName) metaParts.push(opts.contactName);
  if (opts.subtitle) metaParts.push(opts.subtitle);
  metaParts.push(
    `Per ${opts.asOfDate.toLocaleDateString(opts.locale || "no-NO", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    })}`
  );
  pdf.text(metaParts.join(" · "), PAGE.width - margin, baselineY, {
    align: "right",
  });

  // Divider line
  pdf.setDrawColor(...PRIMARY_COLOR);
  pdf.setLineWidth(0.3);
  pdf.line(
    margin,
    margin + HEADER_HEIGHT_MM - 4,
    PAGE.width - margin,
    margin + HEADER_HEIGHT_MM - 4
  );
};

export const exportElementToPdf = async (
  opts: PdfExportOptions
): Promise<void> => {
  // 1. Capture the element. `filter` skips anything tagged data-pdf-ignore
  // so the download button itself doesn't show up.
  const dataUrl = await toPng(opts.element, {
    backgroundColor: "#ffffff",
    pixelRatio: 2,
    cacheBust: true,
    filter: (node) =>
      !(node instanceof Element && node.hasAttribute("data-pdf-ignore")),
  });

  const captured = await loadImage(dataUrl);

  // 2. Build the PDF.
  const pdf = new jsPDF({ unit: "mm", format: "a4", orientation: "landscape" });
  const availableWidthMm = PAGE.width - 2 * PAGE.margin;
  const renderedWidthMm = availableWidthMm * CONTENT_SCALE;
  const sideOffsetMm = PAGE.margin + (availableWidthMm - renderedWidthMm) / 2;
  const fullImgHeightMm =
    (captured.height * renderedWidthMm) / captured.width;

  // First page allowance is smaller because it has the branded header.
  const firstPageContentHeight =
    PAGE.height - 2 * PAGE.margin - HEADER_HEIGHT_MM;
  const restPageContentHeight = PAGE.height - 2 * PAGE.margin;

  const pxPerMm = captured.width / renderedWidthMm;

  let processedHeightMm = 0;
  let isFirstPage = true;

  while (processedHeightMm < fullImgHeightMm) {
    const available = isFirstPage
      ? firstPageContentHeight
      : restPageContentHeight;
    const yPos = isFirstPage ? PAGE.margin + HEADER_HEIGHT_MM : PAGE.margin;

    if (!isFirstPage) pdf.addPage();
    if (isFirstPage) await drawHeader(pdf, opts);

    const remainingMm = fullImgHeightMm - processedHeightMm;
    const sliceHeightMm = Math.min(remainingMm, available);
    const slicePx = Math.round(sliceHeightMm * pxPerMm);

    if (slicePx > 0) {
      const sliceData = slicePngDataUrl(
        captured,
        Math.round(processedHeightMm * pxPerMm),
        slicePx
      );
      pdf.addImage(
        sliceData,
        "PNG",
        sideOffsetMm,
        yPos,
        renderedWidthMm,
        sliceHeightMm,
        undefined,
        "FAST"
      );
    }

    processedHeightMm += sliceHeightMm;
    isFirstPage = false;
  }

  // 3. Save.
  const blob = pdf.output("blob");
  saveAs(blob, opts.filename);
};

export const slugify = (input: string): string =>
  input
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
